import {
  approvalWorkflowTemplateControllerCreateTemplate,
  approvalWorkflowTemplateControllerDeleteTemplate,
  approvalWorkflowTemplateControllerList,
  approvalWorkflowTemplateControllerUpdateTemplate
} from '@api/AwTemplate';
import {
  QueryKey,
  useInfiniteQuery,
  useMutation,
  useQueryClient
} from '@tanstack/react-query';
import {
  ApprovalWorkflowTemplateControllerDeleteTemplateParams,
  CreateAWTemplateDto,
  UpdateAWTemplateDto
} from 'src/api/Api';

export const QUERY_AW_TEMPLATES_LIST = 'approval_aw_templates_list';

export function useAWTemplateList({
  workspaceId,
  enabled = true
}: {
  workspaceId: string;
  enabled?: boolean;
}) {
  const fetch = useInfiniteQuery({
    queryKey: [QUERY_AW_TEMPLATES_LIST, { workspaceId }],
    enabled: !!workspaceId && enabled,
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await approvalWorkflowTemplateControllerList(
        {
          workspaceId: workspaceId || '',
          after: pageParam
        },
        { signal }
      );
      return data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.endCursor : undefined
  });

  return fetch;
}

export function useAWSDeleteTemplate({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (
      data: ApprovalWorkflowTemplateControllerDeleteTemplateParams
    ) => {
      return await approvalWorkflowTemplateControllerDeleteTemplate(data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_AW_TEMPLATES_LIST]
      });
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
}

export function useCreateTemplate({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: CreateAWTemplateDto) => {
      return await approvalWorkflowTemplateControllerCreateTemplate(data);
    },
    onSettled: () => {
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
}

export function useUpdateTemplate({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: UpdateAWTemplateDto) => {
      return await approvalWorkflowTemplateControllerUpdateTemplate(data);
    },
    onSettled: () => {
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
}
